import { useContext, useEffect, useRef, useState } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { Routes } from 'types/routes';

import { LS_JOB_SEARCH_QUERY } from 'consts/localStorage';
import { LIST_MARKUP } from 'consts/redux';
import { checkInLocalStorage, createLocalStorage, destroyLocalStorage, getLocalStorage } from 'utils/localStorage';
import { changeToCent } from 'utils/math';
import { withVariables } from 'utils/string';
import Tracking, { SmEventDescriptons, SmEventTypes } from 'services/Tracking';

import { create as createJobFavourites } from 'requests/jobFavourites';
import { list as listJobs } from 'requests/jobs';
import { create as createJobSearchQuery } from 'requests/jobSearchQuery';

import LocaleContext from 'components/context/Locale';
import Head from 'components/layout/Head';
import PageContainer from 'components/layout/PageContainer';
import SpinnerBlock from 'components/layout/SpinnerBlock';
import JobLoginModal from 'components/modules/public/layout/modals/JobLoginModal';
import LayoutDefault from 'components/modules/public/layouts/Default';
import { ISearchParam, SearchParamCity, SearchParamPage, SearchParamValue, SearchParamValues, useSearchParams } from 'hooks/useSearchParams';

import SectionArticles from './sections/Articles';
import SectionFilters from './sections/Filters';
import SectionList from './sections/List';
import SectionNewsletter from './sections/Newsletter';
import SectionSave from './sections/Save';
import SectionSearch from './sections/Search';
import StyledComponent from './styles';
import { Filters, FitlersSort, Props } from './types';

const PublicPageJobs: NextPage<Props> = ({
    lang,
    data,
    initialSearchParams,
    initialResources,
    state,
    query,
    cmsPage,
}) => {

    const router = useRouter();
    const localeContext = useContext(LocaleContext);
    const locale = localeContext?.translations;

    /**
     * Initial state
     *
     */

    const [saved, setSaved]: [boolean, Function] = useState(false);
    const [isLoginWorkerModalVisible, setIsLoginWorkerModalVisible]: [boolean, Function] = useState(false);
    const [bySaveJobSearchQuery, setBySaveJobSearchQuery]: [boolean, Function] = useState(false);
    const [cityFilterVisible, setCityFilterVisible]: [boolean, Function] = useState(true);
    const [additionalCheckbox, setAdditionalCheckbox]: [boolean, Function] = useState(false);

    const [usedCities, setUsedCities] = useState(
        initialResources.jobCities.concat([initialResources.initialCity])
    );

    const [searchState, setSearchState] = useState({
        ...initialResources,
        isLoaded: true,
        isLoading: false,
        saved: false,
        firstLoad: true,
    });

    const { getSearchParams, getSearchParamsQueryValues, getSearchParamValue, setSearchParamValue, setSearchParamValueFromString } = useSearchParams({

        category: new SearchParamValues('jobCategorySlug', initialSearchParams.jobCategorySlug ),
        agreement: new SearchParamValues('jobAgreementSlug', initialSearchParams.jobAgreementSlug ),
        mode: new SearchParamValues('jobModeSlug', initialSearchParams.jobModeSlug),
        experience: new SearchParamValues('jobExperienceSlug', initialSearchParams.jobExperienceSlug),
        language: new SearchParamValues('jobLanguageSlug', initialSearchParams.jobLanguageSlug ),
        salary: new SearchParamValue('salaryFrom', initialSearchParams.salaryFrom ),

        city: new SearchParamCity('jobCompanyCitySlug', initialResources.initialCity),
        time: new SearchParamValues('jobTimeSlug', initialSearchParams.jobTimeSlug ),
        radius: new SearchParamValue('rangeRadius', initialSearchParams.rangeRadius ),
        name: new SearchParamValue('name', initialSearchParams.name ),

        cvFree: new SearchParamValue('isCvNotRequired', initialSearchParams.isCvNotRequired ),

        sort: new SearchParamValue('sort', initialSearchParams.sort || FitlersSort.Newest ),
        page: new SearchParamPage('page', initialSearchParams.page ),
    });

    const searchParams = getSearchParams();

    const activeCity = searchParams.city.getValue();
    const jobsTotalCount = searchState.jobs.meta?.total;

    const hasActiveFilters = Object.keys(searchParams).some(key => {
        if (key === 'sort' || key === 'page') return false;

        const value = searchParams[key].getValue();
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });

    /**
     * useEffect on router.query
     *
     */
    useEffect(() => {
        if (! searchState.firstLoad) {
            setSaved(false);
            setAdditionalCheckbox(false);
            setSearchParamsFromQuery(router.query);
            search();
        }

        setSearchState( (state) => ({ ...state, firstLoad: false }));
    }, [router.query]);

    /**
     * Set search params from query string
     *
     */
    const setSearchParamsFromQuery = (query: any = {}) => {
        Object.keys(searchParams).map((key) => {
            const value = query[searchParams[key].getQueryKey()];

            if (key == 'page') {
                setSearchParamValue(key, value);
                return;
            }

            switch (key) {
                case 'city':
                    setSearchParamValue(key, usedCities?.find(city => city?.slug === value));
                    break;
                default:
                    setSearchParamValueFromString(key, value);
            }
        });
    };

    /**
     * Update router query string
     *
     * @param attributes: any = {}
     */
    const updateQueryString = (searchParams: any, hash: string = '') => {
        const urlSearchParams = new URLSearchParams();

        Object.keys(searchParams)
            .map(key => {
                const value = searchParams[key].getQueryValue();
                if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length == 0)) return;
                urlSearchParams.set(searchParams[key].getQueryKey(), value);
            });


        router.push({
            pathname: Routes.PublicJobs,
            query: urlSearchParams.toString(),
            hash,
        }, undefined, { shallow: true });
    };

    /**
    * Set search params state
    *
    */
    const setSearchParamsState = (key: string, value: any, clear?: boolean) => {
        const jobAgreementRegex = /umowa-dowolna/g;
        const allAgreement = initialResources.jobAgreements.find(jobAgreement => jobAgreementRegex.test(jobAgreement.slug));

        const jobAgreementValues = getSearchParamValue(key);
        switch (key) {
            case 'agreement':


                if (value === allAgreement.slug) {
                    setSearchParamValue(key, initialResources.jobAgreements.map(agreement => agreement.slug));
                } else
                if (value !== allAgreement.slug) {
                    if (jobAgreementValues.includes(value)) {
                        jobAgreementValues.splice(jobAgreementValues.indexOf(value), 1);
                    } else {
                        jobAgreementValues.push(value);
                    }

                    if (jobAgreementValues.includes(allAgreement.slug)) {
                        jobAgreementValues.splice(jobAgreementValues.indexOf(allAgreement.slug), 1);
                    }

                    setSearchParamValue(key, jobAgreementValues, clear);
                }

                break;

            case 'city':
                setUsedCities( (state) => [...state, value] );
                setSearchParamValue(key, value, clear);
                break;
            default:
                setSearchParamValue(key, value, clear);
        }
    };

    /**
     * Search jobs
     *
     * @param number page = 1
     */
    const search = (page: number = 1) => {
        setSearchState( state => ({
            ...state,
            isLoading: true,
            isLoaded: false,
        }));

        const salaryNumber = Number(getSearchParamValue('salary') );

        // request search
        listJobs({
            perPage: 10,
            ...getSearchParamsQueryValues(),
            salaryFrom: salaryNumber > 0 ? changeToCent( salaryNumber ) : null,
        })
            .then(listCollection => {
                setSearchState( state => ({
                    ...state,
                    isLoading: false,
                    isLoaded: true,
                    jobs: {
                        ...LIST_MARKUP,
                        ...listCollection,
                    },
                }));
            })
            .catch(error => {
                console.error(error);

                setSearchState( state => ({
                    ...state,
                    ...LIST_MARKUP,
                    isLoading: false,
                    isLoaded: false,
                    error: true,
                }));
            });

        // SM tack search query
        if (state?.user?.email) Tracking.salesManago(
            state?.user?.email,
            SmEventTypes.Other,
            SmEventDescriptons.UsedSearch,
            window.location?.toString(),
        );

    };

    const onSearch = (page: number = 1, hash: string = '') => {
        setSearchParamValue('page', page);
        document.getElementById('results').scrollIntoView();

        updateQueryString({
            ...getSearchParams(),
        }, hash);
    };

    useEffect(() => {
        if (bySaveJobSearchQuery) {
            onSaveJobSearchQuery();
            setBySaveJobSearchQuery(false);
        }
    }, [state.user]);


    const handleSuggest = (name: string) => {
        setSearchParamValue('name', name);
        onSearch();
    };

    const handleChangeSearchParam = (key: string, value: any, clear?: boolean) => {
        setSearchParamsState(key, value, clear);
    };

    const handleChangeSearchParamAndSearch = (key: string, value: any, clear?: boolean) => {
        setSearchParamsState(key, value, clear);
        onSearch();
    };

    const handleChangeSearchParams = (searchParams: any) => {
        Object.keys(searchParams)
            .map(key => {
                setSearchParamsState(key, searchParams[key].getValue());
            });
        onSearch();
    };

    const handleChangePage = (page: number = 1) => {
        onSearch(page);
        document.getElementById('results').scrollIntoView();
    };

    const handleClearSearchParams = (keys: string[]) => {
        keys.forEach(key => setSearchParamValue(key, null, true));
        onSearch();
    };

    const handleClearAllFilters = () => {
        handleClearSearchParams(
            Object.keys(searchParams).filter(key => key !== 'sort')
        );
    };

    const onSaveJobSearchQuery = () => {

        if (!state.user) {

            setIsLoginWorkerModalVisible(true);
            setBySaveJobSearchQuery(true);

            createLocalStorage(LS_JOB_SEARCH_QUERY, {

                jobCompanyCitySlug: searchParams.city.getValue()?.slug,
                jobCompanyCityName: searchParams.city.getValue()?.name,
                jobCategorySlug: searchParams.category.getValue().map(slug => searchState.jobCategories.find(value => value.slug === slug)),
                jobAgreementSlug:  searchParams.agreement.getValue().map(slug => searchState.jobAgreements.find(value => value.slug === slug)),
                jobExperienceSlug: searchParams.experience.getValue().map(slug => searchState.jobExperiences.find(value => value.slug === slug)),
                jobModeSlug: searchParams.mode.getValue().map(slug => searchState.jobModes.find(value => value.slug === slug)),
                jobLanguageSlug: searchParams.language.getValue().map(slug => searchState.jobLanguages.find(value => value.slug === slug)),
                jobTimeSlug: searchParams.time.getValue().map(slug => searchState.jobTimes.find(value => value.slug === slug)),

                rangeRadius: searchParams.radius.getValue(),
                name: searchParams.name.getValue(),
                version: 3,
            });
        }

        if (state.user) {
            if (checkInLocalStorage(LS_JOB_SEARCH_QUERY)) {
                createJobSearchQuery({
                    search: JSON.stringify(getLocalStorage(LS_JOB_SEARCH_QUERY)),
                })
                    .then(() => {
                        setSaved(true);
                        setAdditionalCheckbox(true);
                        destroyLocalStorage(LS_JOB_SEARCH_QUERY);
                    })
                    .catch(error => {
                        console.error(error);
                        throw error;
                    });
            } else {
                createJobSearchQuery({
                    search: JSON.stringify({
                        jobCompanyCitySlug: searchParams.city.getValue()?.slug,
                        jobCompanyCityName: searchParams.city.getValue()?.name,
                        jobCategorySlug: searchParams.category.getValue().map(slug => searchState.jobCategories.find(value => value.slug === slug)),
                        jobAgreementSlug:  searchParams.agreement.getValue().map(slug => searchState.jobAgreements.find(value => value.slug === slug)),
                        jobExperienceSlug: searchParams.experience.getValue().map(slug => searchState.jobExperiences.find(value => value.slug === slug)),
                        jobModeSlug: searchParams.mode.getValue().map(slug => searchState.jobModes.find(value => value.slug === slug)),
                        jobLanguageSlug: searchParams.language.getValue().map(slug => searchState.jobLanguages.find(value => value.slug === slug)),
                        jobTimeSlug: searchParams.time.getValue().map(slug => searchState.jobTimes.find(value => value.slug === slug)),

                        rangeRadius: searchParams.radius.getValue(),
                        name: searchParams.name.getValue(),
                        version: 3,
                    }),
                })
                    .then(() => {
                        setSaved(true);
                        setAdditionalCheckbox(true);
                    })
                    .catch(error => {
                        console.error(error);
                        throw error;
                    });
            }
        }
    };

    const onSaveJobFavourites = (id: string, jobCityId: string) => {
        if (!state.user) {
            setIsLoginWorkerModalVisible(true);
        }
        if (state.user) {
            createJobFavourites({
                jobId: id,
                cityId: jobCityId,
            });
        }
    };

    return (
        <StyledComponent className="public-page-jobs">
            <Head
                {...data.meta}
                title={activeCity?.seoTitle || cmsPage?.metaTitle || data.meta?.title}
                description={activeCity?.seoDescription || cmsPage?.metaDescription || data?.meta?.description}
                robots={cmsPage?.metaRobots}
                image={cmsPage?.image?.imageUrl || data?.meta?.image}
            />
            <LayoutDefault>
                <PageContainer>
                    <SectionSearch
                        jobCategories={searchState.jobCategories}
                        jobAgreements={searchState.jobAgreements}
                        jobModes={searchState.jobModes}
                        jobExperiences={searchState.jobExperiences}
                        jobTimes={searchState.jobTimes}
                        jobLanguages={searchState.jobLanguages}

                        searchParams={searchParams}
                        onChangeSearchParam={handleChangeSearchParam}
                        onChangeSearchParams={handleChangeSearchParams}

                        onSuggest={handleSuggest}
                        onSearch={onSearch}

                        onSaveJobSearchQuery={onSaveJobSearchQuery}
                        jobsList={searchState.jobs}

                        additionalCheckbox={additionalCheckbox}
                        setAdditionalCheckbox={ () => setAdditionalCheckbox() }

                        {...data.content.search}
                    />
                    {searchState.jobs.elements?.length > 0 && (

                        <SectionFilters
                            {...data.content.filters}
                            cities={searchState.jobCities}

                            searchParams={searchParams}
                            onChangeSearchParam={handleChangeSearchParamAndSearch}
                        />

                    )}

                    <div id="results" />

                    <SpinnerBlock loading={searchState.isLoading} >

                        <SectionList
                            header={{
                                ...data.content.list.header,
                                translations: {
                                    ...data.content.list.header.translations,

                                    title: {
                                        ...data.content.list.header.translations.title,

                                        default: (jobsTotalCount > 0 && activeCity ) ?
                                            withVariables(data.content.list.header.translations.title?.default, {
                                                city: activeCity.name,
                                                jobCount: jobsTotalCount,
                                            }) : '',

                                        withValues: (activeCity) ?
                                            withVariables(data.content.list.header.translations.title?.withValues, {
                                                city: activeCity.name,
                                                jobCount: jobsTotalCount,
                                            }) : '',


                                        /* label: activeCity || jobsTotalCount > 0 ? data.content.list.header.translations.title.label : '', */
                                    },
                                },
                                jobCategories: searchState.jobCategories,
                                jobAgreements: searchState.jobAgreements,
                                jobModes: searchState.jobModes,
                                jobExperiences: searchState.jobExperiences,
                                jobLanguages: searchState.jobLanguages,
                                jobTimes: searchState.jobTimes,

                                searchParams,
                                onChangeSearchParam: handleChangeSearchParamAndSearch,
                                onClearFilters: handleClearSearchParams,
                                onClearAll: handleClearAllFilters,
                                onSearch,

                                collection: searchState.jobs,
                            }}
                            list={{
                                ...data.content.list.list,
                                collection: searchState.jobs,
                                onSaveJobFavourites,
                                onSaveJobSearchQuery,
                                onClearFilters: handleClearAllFilters,
                                hasActiveFilters,
                                onChangePage: handleChangePage,
                                savedSearch: saved,
                                isLoaded: searchState.isLoaded,
                                profile: state?.user,
                            }}
                        />

                    </SpinnerBlock>

                    <SectionSave
                        {...data.content.save}
                        onSave={onSaveJobSearchQuery}
                        saved={saved}
                    />

                    {jobsTotalCount > 0 && (
                        <SectionArticles
                            {...data.content.articles}
                            articles={initialResources.articles}
                        />
                    )}
                    <SectionNewsletter  {...data.content.newsletter} />

                    {isLoginWorkerModalVisible && (
                        <div className="search-modal">
                            <JobLoginModal
                                {...locale.navigation.loginWorkerModalProps}
                                modalProps={{
                                    ...locale.navigation.loginWorkerModalProps.modalProps,
                                    onClose: () => {
                                        setIsLoginWorkerModalVisible(false);
                                    },
                                }}
                                onSuccess={() => {
                                    setIsLoginWorkerModalVisible(false);
                                }}

                            />
                        </div>
                    )}
                </PageContainer>
            </LayoutDefault>
        </StyledComponent>
    );
};

export default PublicPageJobs;
